/**
 * What's New Slide-over Panel
 * WHY: In-app changelog viewer for logged-in users
 */

import { useState, useEffect } from "react";
import { Loader2, Megaphone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { format } from "date-fns";

interface ChangelogEntry {
  id: string;
  title: string;
  body: string;
  version: string;
  tags: string[];
  breaking_change: boolean;
  published_at: string;
}

const TAG_COLORS: Record<string, string> = {
  new: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  improved: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  fixed: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  breaking: "bg-red-500/20 text-red-400 border-red-500/30",
  deprecated: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
  token_added: "bg-purple-500/20 text-purple-400 border-purple-500/30",
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userTier: string;
  userId: string;
}

export function WhatsNewPanel({ open, onOpenChange, userTier, userId }: Props) {
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      setLoading(true);
      const { data } = await (supabase as any)
        .from("changelog_entries")
        .select("id, title, body, version, tags, breaking_change, published_at")
        .eq("is_published", true)
        .order("published_at", { ascending: false });

      // Filter by tier client-side
      const filtered = (data || []).filter((e: any) =>
        !e.visible_tiers?.length || e.visible_tiers.includes(userTier)
      );
      setEntries(filtered);
      setLoading(false);

      // Mark as seen
      await (supabase as any)
        .from("profiles")
        .update({ last_seen_changelog: new Date().toISOString() })
        .eq("id", userId);
    };
    load();
  }, [open, userTier, userId]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-primary" />
            What's New
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : entries.length === 0 ? (
            <p className="text-muted-foreground text-center py-12 text-sm">No updates yet.</p>
          ) : (
            entries.map((entry) => (
              <div key={entry.id} className="border-l-2 border-primary/30 pl-4 pb-1">
                <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                  <Badge variant="outline" className="text-[10px] font-mono">
                    {entry.version}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(entry.published_at), "MMM dd, yyyy")}
                  </span>
                  {entry.tags?.map((tag) => (
                    <Badge key={tag} className={`text-[9px] border ${TAG_COLORS[tag] || ""}`}>
                      {tag}
                    </Badge>
                  ))}
                  {entry.breaking_change && (
                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30 border text-[9px] font-bold">
                      BREAKING
                    </Badge>
                  )}
                </div>
                <h3 className="font-semibold text-sm text-foreground mb-1">{entry.title}</h3>
                <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed">
                  {entry.body}
                </p>
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
